import { useEffect, useState } from "react";
import { useSync } from "@/lib/live";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, XCircle, LifeBuoy, MapPin, FileDown, ShieldAlert } from "lucide-react";
import { api, errMsg, API } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, BigButton } from "@/components/Layout";
import { StatusBadge, StatusSteps } from "@/components/Status";
import { BookingSummary } from "@/components/BookingSummary";
import { PartnerCard } from "@/components/PartnerCard";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { BookingChat } from "@/components/BookingChat";
import { RepeatPicker, PrepTips } from "@/components/PickupExtras";
import { RatePartner } from "@/components/RatePartner";
import { MissedRecovery } from "@/components/MissedRecovery";

export default function BookingDetail() {
  const { id } = useParams();
  const { t, refreshSummary, user } = useApp();
  const nav = useNavigate();
  const [b, setB] = useState(null);
  const [del, setDel] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useSync(() => api.get(`/bookings/${id}`).then((r) => setB(r.data)).catch(() => {}));
  useEffect(() => {
    if (!b || !["Requested", "Confirmed"].includes(b.status)) return;
    const timer = setInterval(() => api.get(`/bookings/${id}`).then((r) => setB(r.data)).catch(() => {}), 60000);
    return () => clearInterval(timer);
  }, [b?.status]); // eslint-disable-line

  useEffect(() => {
    api.get(`/bookings/${id}`).then((r) => setB(r.data)).catch((e) => { toast.error(errMsg(e)); nav("/history"); });
  }, [id]); // eslint-disable-line

  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${id}/cancel`, { reason });
      setB(data);
      setDel(false);
      toast.success(t("Pickup cancelled. It stays in your history."));
      refreshSummary();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!b) return <p className="text-[#7B968B]">{t("Loading…")}</p>;
  const editable = !["Completed", "Cancelled"].includes(b.status);

  return (
    <div className="space-y-5">
      <button onClick={() => nav("/history")} className="flex items-center gap-1 text-sm font-semibold text-[#2E9E6B]" data-testid="detail-back-btn">
        <ArrowLeft className="h-4 w-4" /> {t("History")}
      </button>
      {b.dispatch?.state === "queued" && !b.staff && <p className="rounded-2xl bg-[#EEF5FF] p-3 text-sm text-[#1E3A8A]" data-testid="booking-queued-note">{t("Booking received. We'll assign the nearest partner as soon as service opens in the morning.")}</p>}
      {b.dispatch?.state === "admin" && !b.staff && ["Requested", "Confirmed"].includes(b.status) && <p className="rounded-2xl bg-[#FEF3C7] p-3 text-sm text-[#92400E]" data-testid="booking-waiting-note">{t("All nearby partners are busy right now. Your pickup is saved and will go to the nearest partner the moment one is free.")}</p>}
      <MissedRecovery b={b} onDone={() => { api.get(`/bookings/${id}`).then((r) => setB(r.data)); refreshSummary(); }} />
      <Card className="space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[#7B968B]">{t("Booking ID")}</p>
            <p className="font-display text-2xl font-extrabold text-[#0D3B2B]" data-testid="detail-booking-id">{b.booking_id}</p>
          </div>
          <StatusBadge status={b.status} testid="detail-status" />
        </div>
        <StatusSteps status={b.status} />
        {b.status === "Completed" && (
          <div className="rounded-2xl bg-[#D1FAE5] p-4 text-[#065F46]" data-testid="detail-actual-weights">
            <p className="font-bold">{t("Collected")}: {b.total_kg} kg</p>
            {b.line_items?.length ? b.line_items.map((x) => (
              <p key={x.material} className="flex justify-between text-sm" data-testid={`detail-line-${x.material.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <span>{t(x.material)} · {x.kg} kg × ₹{x.rate}</span><span className="font-semibold">₹{x.amount}</span>
              </p>
            )) : <p className="text-sm">{Object.entries(b.actual_weights || {}).map(([k, v]) => `${t(k)} ${v} kg`).join(" · ")}</p>}
          </div>
        )}
        {b.status === "Completed" && b.amount_paid > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="detail-amount-paid">
            <p className="font-display text-lg font-bold text-[#0D3B2B]">{t("Amount for this pickup")}: ₹{b.amount_paid}</p>
            <StatusBadge status={b.payment_status || "Pending"} testid="detail-payment-status" />
            {b.payment_status === "Paid" && b.payment_ref && <span className="text-xs text-[#4A6358]">Ref {b.payment_ref}</span>}
          </div>
        )}
        {b.status === "Completed" && <RatePartner b={b} onDone={() => api.get(`/bookings/${id}`).then((r) => setB(r.data))} />}
        {b.status === "Completed" && (
          <a href={`${API}/bookings/${b.booking_id}/receipt`} data-testid="detail-receipt-btn"
            className="flex h-12 items-center justify-center gap-2 rounded-2xl bg-[#0D3B2B] font-semibold text-white"><FileDown className="h-5 w-5" />{t("Download receipt (PDF)")}</a>
        )}
        {editable && user.customer_type && user.customer_type !== "Household" && user.kyc?.status !== "Approved" && (
          <Link to="/profile/kyc" className="flex items-start gap-2 rounded-2xl bg-[#FEF3C7] p-3 text-sm text-[#92400E]" data-testid="detail-kyc-banner">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />{t("Your business KYC is not approved yet. We can confirm this pickup after your documents are verified. Tap to upload.")}
          </Link>
        )}
        {b.status === "Cancelled" && (
          <p className="rounded-2xl bg-[#FEE2E2] p-3 text-sm text-[#991B1B]" data-testid="detail-cancel-info">
            {b.cancelled_by === "admin" ? t("Cancelled by the Kindsaver team.") : t("You cancelled this pickup.")}{b.cancel_reason ? ` ${t("Reason")}: ${b.cancel_reason}` : ""}
          </p>
        )}
      </Card>
      <PartnerCard staff={b.staff} status={b.status} trip={b.trip_status} />
      {editable && (
        <Card className="space-y-4" data-testid="detail-repeat-card">
          <RepeatPicker value={b.repeat || ""} testid="detail-repeat" onChange={(repeat) => api.patch(`/bookings/${id}/repeat`, { repeat })
            .then(() => { setB((x) => ({ ...x, repeat })); toast.success(repeat ? t("Repeat pickup turned on") : t("Repeat pickup stopped")); }).catch((e) => toast.error(errMsg(e)))} />
          <PrepTips types={b.scrap_types} />
        </Card>
      )}
      <Card className="space-y-3">
        <p className="font-display text-lg font-bold text-[#0D3B2B]">{t("Messages")}</p>
        <p className="text-xs text-[#4A6358]">{t("Chat with your pickup partner and Kindsaver support about this pickup.")}</p>
        <BookingChat messages={b.messages || []} me="customer" postUrl={`/bookings/${id}/messages`} onSent={(messages) => setB((x) => ({ ...x, messages }))} testid="detail-chat" />
      </Card>
      <BookingSummary b={b} />
      <a href={`https://www.google.com/maps?q=${b.lat},${b.lng}`} target="_blank" rel="noreferrer" data-testid="detail-map-link"
        className="flex items-center gap-2 text-sm font-semibold text-[#2E9E6B]"><MapPin className="h-4 w-4" />{t("Open location in maps")}</a>

      {editable && (
        <div className="grid grid-cols-2 gap-3">
          <BigButton onClick={() => nav(`/history/${id}/edit`)} data-testid="detail-edit-btn"><Pencil className="h-5 w-5" />{t("Edit")}</BigButton>
          <BigButton coral onClick={() => setDel(true)} data-testid="detail-cancel-btn"><XCircle className="h-5 w-5" />{t("Cancel pickup")}</BigButton>
        </div>
      )}
      <Link to={`/help?booking=${b.booking_id}`} data-testid="detail-help-link"
        className="flex h-12 items-center justify-center gap-2 rounded-2xl border border-[#D2E0D8] bg-white font-semibold text-[#0D3B2B]">
        <LifeBuoy className="h-5 w-5" /> {t("Help and complaints")}
      </Link>
      <ConfirmDialog open={del} onOpenChange={setDel} danger busy={busy} onYes={remove}
        title={t("Are you sure you want to cancel this pickup?")}
        lines={[`${b.booking_id} · ${b.date} · ${b.slot_label}`, t("It will stay in your history as Cancelled. You can also just change the date instead.")]}>
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("Reason (optional)")} data-testid="cancel-reason-input"
          className="h-12 w-full rounded-xl border border-[#D2E0D8] bg-white px-4 text-base" />
      </ConfirmDialog>
    </div>
  );
}
